// ── company-archive-modal: the archive confirm Modal (PATCH status=archived) ─
// Wave 1 (Companies). Archiving is a PATCH that sets status:"archived" — there
// is NO destructive delete. This modal is a deliberate confirmation step so an
// archive is never a one-click accident; the page owns the actual PATCH and
// toasts only after the backend confirms. A refused write (permission-denied /
// gate-closed / unavailable) reads as its honest message inline and the dialog
// stays open — a closed write NEVER implies the company was archived.

import { Alert, Button, Modal } from "@vjal/ui";

import { destructiveButtonClass } from "../../components/ui-classes";
import type { CoachingCompanyDto } from "../../services/coaching-dtos";
import { describeCompanyWriteError } from "./companies-write";

export interface CompanyArchiveModalProps {
  open: boolean;
  company: CoachingCompanyDto | null;
  onClose: () => void;
  /** The actual archive PATCH — owned by the page (react-query mutation). */
  onConfirm: () => Promise<unknown>;
  submitting?: boolean;
  submitError?: unknown;
}

export function CompanyArchiveModal({
  open,
  company,
  onClose,
  onConfirm,
  submitting = false,
  submitError
}: CompanyArchiveModalProps) {
  const topError = describeCompanyWriteError(submitError);
  const name = company?.name || "this company";

  return (
    <Modal open={open} onClose={onClose} title="Archive company">
      <div className="flex flex-col gap-4">
        {topError && <Alert variant={topError.tone === "warning" ? "warning" : "error"}>{topError.message}</Alert>}

        <Alert variant="warning">
          Archiving <span className="font-semibold">{name}</span> removes it from active navigation. Linked programs and
          cohorts stay intact; you can reactivate the company later from its edit form.
        </Alert>

        <div className="mt-2 flex items-center justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="danger"
            className={destructiveButtonClass}
            isLoading={submitting}
            onClick={() => {
              void onConfirm().catch(() => {
                // The owning mutation surfaces the error via `submitError`;
                // swallow so the dialog stays open with the inline error.
              });
            }}
          >
            Archive company
          </Button>
        </div>
      </div>
    </Modal>
  );
}
